import { useState, useEffect, useMemo, useCallback } from "react";
import { useACLContext } from "../context";
import type { PermissionAction, ResourceType } from "@shield-acl/core";

/**
 * Estado reativo de uma permissão
 */
export interface PermissionState {
  /** Valor atual da permissão */
  allowed: boolean;

  /** Está carregando/processando */
  loading: boolean;

  /** Erro se houver */
  error: Error | null;

  /** Timestamp da última verificação */
  lastChecked: number;

  /** Força uma nova verificação */
  refresh: () => void;
}

/**
 * Opções para o hook usePermissionState
 */
export interface UsePermissionStateOptions {
  /** Intervalo de auto-atualização em ms (0 = desabilitado) */
  refreshInterval?: number;

  /** Callback quando a permissão muda */
  onChange?: (newValue: boolean, oldValue: boolean) => void;

  /** Suspender verificações quando false */
  enabled?: boolean;

  /** Valor inicial enquanto carrega */
  initialValue?: boolean;
}

/**
 * Hook que cria um estado reativo de permissão que auto-atualiza quando user/contexto muda
 * Útil para componentes que precisam reagir a mudanças de permissões em tempo real
 *
 * @param action - Ação a monitorar
 * @param resource - Recurso opcional
 * @param context - Contexto adicional
 * @param options - Opções de configuração
 * @returns Estado reativo da permissão
 *
 * @example
 * ```tsx
 * // Estado reativo simples
 * function FeatureToggle() {
 *   const permission = usePermissionState("feature.premium")
 *
 *   if (permission.loading) {
 *     return <Spinner />
 *   }
 *
 *   return (
 *     <div>
 *       <Switch checked={permission.allowed} disabled />
 *       <span>Premium Feature: {permission.allowed ? "Enabled" : "Disabled"}</span>
 *       <button onClick={permission.refresh}>Recheck</button>
 *     </div>
 *   )
 * }
 *
 * // Com auto-refresh e callback
 * function LivePermissionMonitor() {
 *   const [log, setLog] = useState<string[]>([])
 *
 *   const permission = usePermissionState("admin.access", undefined, undefined, {
 *     refreshInterval: 5000, // Verifica a cada 5 segundos
 *     onChange: (newValue, oldValue) => {
 *       setLog(prev => [...prev, `Changed from ${oldValue} to ${newValue} at ${new Date().toLocaleTimeString()}`])
 *     }
 *   })
 *
 *   return (
 *     <div>
 *       <h3>Admin Access: {permission.allowed ? "✓" : "✗"}</h3>
 *       <p>Last checked: {new Date(permission.lastChecked).toLocaleTimeString()}</p>
 *       <ul>
 *         {log.map((entry, i) => <li key={i}>{entry}</li>)}
 *       </ul>
 *     </div>
 *   )
 * }
 * ```
 */
export function usePermissionState<TContext = any>(
  action: PermissionAction,
  resource?: ResourceType,
  context?: TContext,
  options: UsePermissionStateOptions = {},
): PermissionState {
  const { can, user } = useACLContext<TContext>();
  const {
    refreshInterval = 0,
    onChange,
    enabled = true,
    initialValue = false,
  } = options;

  const [state, setState] = useState<PermissionState>({
    allowed: initialValue,
    loading: true,
    error: null,
    lastChecked: 0,
    refresh: () => {},
  });

  // Função para verificar permissão
  const checkPermission = useCallback(() => {
    if (!enabled) return;

    try {
      const newValue = can(action, resource, context);
      const now = Date.now();

      setState((prev) => {
        // Se o valor mudou, chama onChange
        if (prev.allowed !== newValue && !prev.loading && onChange) {
          onChange(newValue, prev.allowed);
        }

        return {
          allowed: newValue,
          loading: false,
          error: null,
          lastChecked: now,
          refresh: prev.refresh,
        };
      });
    } catch (error) {
      setState((prev) => ({
        ...prev,
        loading: false,
        error: error as Error,
        lastChecked: Date.now(),
      }));
    }
  }, [can, action, resource, context, enabled, onChange]);

  // Atualiza quando user, action, resource ou context mudam
  useEffect(() => {
    checkPermission();
  }, [checkPermission, user]);

  // Auto-refresh se configurado
  useEffect(() => {
    if (refreshInterval > 0 && enabled) {
      const interval = setInterval(checkPermission, refreshInterval);
      return () => clearInterval(interval);
    }
  }, [refreshInterval, checkPermission, enabled]);

  // Atualiza função refresh no estado
  useEffect(() => {
    setState((prev) => ({
      ...prev,
      refresh: checkPermission,
    }));
  }, [checkPermission]);

  return state;
}

/**
 * Hook que monitora múltiplas permissões com estado reativo
 *
 * @param permissions - Array de permissões para monitorar
 * @param options - Opções de configuração
 * @returns Mapa de estados de permissão
 *
 * @example
 * ```tsx
 * const permissions = useMultiplePermissionStates([
 *   { action: "posts.create", key: "createPost" },
 *   { action: "posts.edit", resource: "posts", key: "editPost" },
 *   { action: "posts.delete", resource: "posts", key: "deletePost" }
 * ])
 *
 * return (
 *   <div>
 *     <Button
 *       disabled={!permissions.createPost.allowed}
 *       loading={permissions.createPost.loading}
 *     >
 *       Create Post
 *     </Button>
 *
 *     <Button
 *       disabled={!permissions.editPost.allowed}
 *       variant={permissions.editPost.allowed ? "primary" : "secondary"}
 *     >
 *       Edit Post
 *     </Button>
 *   </div>
 * )
 * ```
 */
export function useMultiplePermissionStates<TContext = any>(
  permissions: Array<{
    action: PermissionAction;
    resource?: ResourceType;
    context?: TContext;
    key: string;
  }>,
  options: Omit<UsePermissionStateOptions, "onChange"> = {},
): Record<string, PermissionState> {
  const { can, user } = useACLContext<TContext>();
  const { refreshInterval = 0, enabled = true, initialValue = false } = options;

  const [states, setStates] = useState<Record<string, PermissionState>>(() => {
    const initial: Record<string, PermissionState> = {};
    permissions.forEach(({ key }) => {
      initial[key] = {
        allowed: initialValue,
        loading: true,
        error: null,
        lastChecked: 0,
        refresh: () => {},
      };
    });
    return initial;
  });

  // Função para verificar todas as permissões
  const checkAllPermissions = useCallback(() => {
    if (!enabled) return;

    const newStates: Record<string, PermissionState> = {};

    permissions.forEach(({ action, resource, context, key }) => {
      try {
        const allowed = can(action, resource, context);
        newStates[key] = {
          allowed,
          loading: false,
          error: null,
          lastChecked: Date.now(),
          refresh: () => {},
        };
      } catch (error) {
        newStates[key] = {
          allowed: false,
          loading: false,
          error: error as Error,
          lastChecked: Date.now(),
          refresh: () => {},
        };
      }
    });

    setStates((prev) => {
      // Mantém as funções refresh existentes
      Object.keys(newStates).forEach((key) => {
        if (prev[key]) {
          newStates[key].refresh = prev[key].refresh;
        }
      });
      return newStates;
    });
  }, [can, permissions, enabled]);

  // Atualiza quando user ou permissões mudam
  useEffect(() => {
    checkAllPermissions();
  }, [checkAllPermissions, user]);

  // Auto-refresh se configurado
  useEffect(() => {
    if (refreshInterval > 0 && enabled) {
      const interval = setInterval(checkAllPermissions, refreshInterval);
      return () => clearInterval(interval);
    }
  }, [refreshInterval, checkAllPermissions, enabled]);

  // Atualiza funções refresh
  useEffect(() => {
    setStates((prev) => {
      const updated = { ...prev };
      Object.keys(updated).forEach((key) => {
        updated[key] = {
          ...updated[key],
          refresh: checkAllPermissions,
        };
      });
      return updated;
    });
  }, [checkAllPermissions]);

  return states;
}

/**
 * Hook que retorna um estado booleano reativo para uma permissão
 * Versão simplificada do usePermissionState
 *
 * @param action - Ação a verificar
 * @param resource - Recurso opcional
 * @param context - Contexto adicional
 * @returns Tupla [allowed, loading, refresh]
 *
 * @example
 * ```tsx
 * const [canEdit, loading, refresh] = usePermissionBoolean("posts.edit", "posts")
 *
 * if (loading) return <Spinner />
 *
 * return (
 *   <Button disabled={!canEdit} onClick={handleEdit}>
 *     Edit Post
 *   </Button>
 * )
 * ```
 */
export function usePermissionBoolean<TContext = any>(
  action: PermissionAction,
  resource?: ResourceType,
  context?: TContext,
): [boolean, boolean, () => void] {
  const state = usePermissionState(action, resource, context);
  return [state.allowed, state.loading, state.refresh];
}
